import { useState } from 'react'
import { motion as Motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { useAuth } from '../hooks/useAuth'
import { useAppSettings } from '../hooks/useAppSettings'
import { useCurrentAdvisor } from '../hooks/useCurrentAdvisor'
import {
  LayoutDashboard,
  Briefcase,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Monitor,
  ChevronRight,
  ChevronDown,
  LogOut,
  ClipboardList,
  Inbox,
} from 'lucide-react'

// ── Design tokens ──────────────────────────────────────────────
const getLightColors = () => ({
  sidebar: '#f5f5f5',
  border: '#e0e0e0',

  iconBg: '#e9e9e9',
  iconColor: '#aaaaaa',

  text: '#555555',
  textMuted: '#999999',

  // selected state — soft grey only, no orange
  activeBg: '#e2e2e2',
  activeIconBg: '#d6d6d6',
  activeText: '#333333',
  activeIcon: '#F5A623',

  // admin label accent (only the text, nowhere else)
  adminText: '#E6A83C',

  hoverBg: 'rgba(0,0,0,0.04)',
  submenuBg: '#eeeeee',
  submenuBorder: '#e2e2e2',
  submenuActiveBg: '#e2e2e2',
})

const getDarkColors = () => ({
  sidebar: '#1e293b',
  border: '#334155',

  iconBg: '#475569',
  iconColor: '#94a3b8',

  text: '#cbd5e1',
  textMuted: '#64748b',

  activeBg: '#334155',
  activeIconBg: '#475569',
  activeText: '#f1f5f9',
  activeIcon: '#F5A623',

  adminText: '#F59E0B',

  hoverBg: 'rgba(255,255,255,0.05)',
  submenuBg: '#0f172a',
  submenuBorder: '#334155',
  submenuActiveBg: '#334155',
})

export function Sidebar({ currentPage, onNavigate, isCollapsed, canView, isDarkMode = false, isMobileOpen = false, onMobileClose, userEmail = '', claimsUnreadCount = 0, tasksUnreadCount = 0, inboxUnreadCount = 0 }) {
  const { canAccess } = useAuth()
  const { signOut } = useAuth()
  const { isEnabled } = useAppSettings()
  const { advisor: currentAdvisor } = useCurrentAdvisor()
  const C = isDarkMode ? getDarkColors() : getLightColors()
  const isAdmin = userEmail === 'test@sfpwealth.co.za' || currentAdvisor?.is_owner === true
  const shouldReduceMotion = useReducedMotion()

  function IconCircle({ icon: CircleIcon, active = false, admin = false }) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: active ? C.activeIconBg : C.iconBg,
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        <CircleIcon
          size={15}
          color={active ? C.activeIcon : admin ? C.adminText : C.iconColor}
          strokeWidth={1.6}
        />
      </span>
    )
  }

  const getInitialExpanded = () => {
    if (['boardroom_tv', 'dashboards_key_metrics'].includes(currentPage)) return 'dashboards_group'
    if (['policies', 'policy_transactions', 'commission_report', 'commissions', 'providers'].includes(currentPage)) return 'policies_group'
    if (['entities', 'clients'].includes(currentPage)) return 'client'
    if (['users', 'roles', 'task_sections'].includes(currentPage)) return 'admin'
    if (currentPage === 'claims_tasks' || currentPage === 'tasks') return null
    return null
  }
  const [expandedMenu, setExpandedMenu] = useState(getInitialExpanded)

  const toggleMenu = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId)
  }

  const allMenuItems = [
    { id: 'clients', label: 'Home', icon: LayoutDashboard },
    {
      id: 'dashboards_group',
      label: 'Dashboards',
      icon: Monitor,
      submenu: [
        { id: 'boardroom_tv', label: 'Boardroom TV [proposed dashboards]' },
        { id: 'dashboards_key_metrics', label: 'Key Metrics [proposed dashboards]' },
      ],
    },
    {
      id: 'client',
      label: 'Client',
      icon: Briefcase,
      submenu: [
        { id: 'entities', label: 'Entities' },
        { id: 'clients', label: 'Natural Person' },
      ],
    },
    {
      id: 'policies_group',
      label: 'Policies',
      icon: ScrollText,
      submenu: [
        { id: 'policies', label: 'Policies' },
        { id: 'policy_transactions', label: 'Policy Transactions' },
        { id: 'commission_report', label: 'Commission Report' },
        { id: 'providers', label: 'Product Providers' },
      ],
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: ShieldCheck,
      isAdmin: true,
      submenu: [
        { id: 'users', label: 'Users' },
        { id: 'roles', label: 'Roles' },
        { id: 'task_sections', label: 'Task Sections' },
      ],
    },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'inbox', label: 'Inbox', icon: Inbox },
    { id: 'claims_tasks', label: 'Dispatch Claims', icon: ClipboardList },
    { id: 'settings', label: 'Settings', icon: SlidersHorizontal },
  ]

  // Filter submenu items to hide commission_report and policy_transactions if user doesn't have commissions_view_all
  const getFilteredSubmenu = (item) => {
    if (!item.submenu) return item.submenu
    if (canAccess('commissions_view_all')) return item.submenu
    return item.submenu.filter(sub => sub.id !== 'commission_report' && sub.id !== 'policy_transactions')
  }

  const menuItems = allMenuItems.filter(item => {
    // Feature-flag gates: hidden for non-admins when disabled
    if (item.id === 'tasks'        && !isEnabled('feature_tasks')        && !isAdmin) return false
    if (item.id === 'claims_tasks' && !isEnabled('feature_claims_tasks') && !isAdmin) return false
    if (!canView) return true
    if (item.submenu) {
      const filteredSubs = getFilteredSubmenu(item) || []
      return filteredSubs.some(sub => canView(sub.id)) || canView(item.id)
    }
    return canView(item.id)
  })

  const w = isCollapsed ? 64 : 280

  return (
    <div
      className={`fixed inset-y-0 left-0 z-30 lg:relative lg:z-auto w-[280px] lg:w-auto transform transition-transform duration-300 lg:transform-none ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}
      style={{
        width: w,
        minWidth: w,
        background: C.sidebar,
        borderRight: `1px solid ${C.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      <nav
        style={{
          flex: 1,
          padding: isCollapsed ? '16px 8px' : '20px 10px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Mobile close button */}
        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="lg:hidden self-end text-sm font-semibold p-1 rounded"
            title="Close menu"
            style={{
              color: C.text,
              marginBottom: '8px'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.hoverBg }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            ✕
          </button>
        )}
        {menuItems.map((item) => {
          const isExpanded = expandedMenu === item.id
          const isLeafActive = !item.submenu && currentPage === item.id

          return (
            <div key={item.id}>
              <button
                onClick={() => item.submenu ? toggleMenu(item.id) : onNavigate(item.id)}
                title={isCollapsed ? item.label : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: isCollapsed ? '8px 0' : '8px 10px',
                  paddingLeft: isCollapsed ? '0' : isLeafActive ? '7px' : '10px',
                  borderRadius: 8,
                  border: 'none',
                  borderLeft: isLeafActive ? '3px solid #F5A623' : '3px solid transparent',
                  cursor: 'pointer',
                  background: isLeafActive ? C.activeBg : 'transparent',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  transition: 'background 0.15s',
                  outline: 'none',
                }}
                onMouseEnter={e => { if (!isLeafActive) e.currentTarget.style.background = C.hoverBg }}
                onMouseLeave={e => { e.currentTarget.style.background = isLeafActive ? C.activeBg : 'transparent' }}
              >
                {/* Icon with dot-badge when sidebar is collapsed */}
                {(() => {
                  const dotCount = item.id === 'claims_tasks' ? claimsUnreadCount
                                 : item.id === 'tasks'        ? tasksUnreadCount
                                 : item.id === 'inbox'        ? inboxUnreadCount
                                 : 0
                  return (
                    <span style={{ position: 'relative', flexShrink: 0 }}>
                      <IconCircle icon={item.icon} active={isLeafActive} admin={item.isAdmin} />
                      {isCollapsed && dotCount > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#E6A83C',
                          border: `2px solid ${C.sidebar}`,
                        }} />
                      )}
                    </span>
                  )
                })()}

                {!isCollapsed && (
                  <>
                    <span style={{
                      flex: 1,
                      fontSize: 13.5,
                      fontWeight: isLeafActive ? 600 : 450,
                      color: item.isAdmin ? C.adminText : isLeafActive ? C.activeText : C.text,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      letterSpacing: 0.1,
                    }}>
                      {item.label}
                    </span>

                    {/* Unread badge — Tasks and Dispatch Claims */}
                    {(() => {
                      const n = item.id === 'claims_tasks' ? claimsUnreadCount
                              : item.id === 'tasks'        ? tasksUnreadCount
                              : item.id === 'inbox'        ? inboxUnreadCount
                              : 0
                      return n > 0 ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 18,
                          height: 18,
                          padding: '0 5px',
                          borderRadius: 9,
                          background: '#E6A83C',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}>
                          {n > 99 ? '99+' : n}
                        </span>
                      ) : null
                    })()}

                    {item.submenu
                      ? isExpanded
                        ? <ChevronDown size={13} color={item.isAdmin ? C.adminText : C.textMuted} strokeWidth={1.8} />
                        : <ChevronRight size={13} color={item.isAdmin ? C.adminText : C.textMuted} strokeWidth={1.8} />
                      : item.id !== 'clients' && item.id !== 'settings'
                        ? <ChevronRight size={13} color={C.textMuted} strokeWidth={1.8} />
                        : null
                    }
                  </>
                )}
              </button>

              {/* Submenu */}
              <AnimatePresence initial={false}>
                {item.submenu && isExpanded && !isCollapsed && (
                  <Motion.div
                    initial={shouldReduceMotion ? false : { height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={shouldReduceMotion ? {} : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden', margin: '3px 0 5px 14px' }}
                  >
                    <div style={{
                      background: C.submenuBg,
                      borderRadius: 8,
                      border: `1px solid ${C.submenuBorder}`,
                    }}>
                      {getFilteredSubmenu(item)?.map((sub, idx) => {
                        const subActive = currentPage === sub.id
                        const filteredSubmenu = getFilteredSubmenu(item) || []
                        return (
                          <button
                            key={sub.id}
                            onClick={() => onNavigate(sub.id)}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 14px',
                              paddingLeft: subActive ? '11px' : '14px',
                              border: 'none',
                              borderLeft: subActive ? '3px solid #F5A623' : '3px solid transparent',
                              borderBottom: idx < filteredSubmenu.length - 1 ? `1px solid ${C.submenuBorder}` : 'none',
                              cursor: 'pointer',
                              background: subActive ? C.submenuActiveBg : 'transparent',
                              fontSize: 13,
                              fontWeight: subActive ? 600 : 400,
                              color: subActive ? C.activeText : C.text,
                              transition: 'background 0.15s',
                              outline: 'none',
                            }}
                            onMouseEnter={e => { if (!subActive) e.currentTarget.style.background = C.hoverBg }}
                            onMouseLeave={e => { e.currentTarget.style.background = subActive ? C.submenuActiveBg : 'transparent' }}
                          >
                            {sub.label}
                          </button>
                        )
                      })}
                    </div>
                  </Motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </nav>

      {/* Sign Out */}
      <div style={{ padding: isCollapsed ? '8px' : '10px', borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={signOut}
          title={isCollapsed ? 'Sign Out' : undefined}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: 10,
            padding: isCollapsed ? '8px 0' : '8px 10px',
            borderRadius: 8,
            border: '1px solid #e8c8c8',
            background: '#fdf0f0',
            cursor: 'pointer',
            color: '#b94040',
            fontSize: 13,
            fontWeight: 500,
            transition: 'background 0.15s',
            outline: 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f8dada' }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fdf0f0' }}
        >
          <LogOut size={14} strokeWidth={1.6} />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  )
}
